import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import PortalLayout from "../components/PortalLayout";
import { api, formatApiError } from "../lib/api";
import { useMember } from "../context/MemberContext";
import { toast } from "sonner";

export default function Contact() {
    const location = useLocation();
    const { member } = useMember();
    const [form, setForm] = useState({ name: "", email: "", message: "" });
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false);
    const [error, setError] = useState("");

    // Pre-fill the form when the user lands here from a deep link
    // (e.g. "Request a change" on /selection passes ?topic=reselect&angel=NNN).
    // Member name/email always pre-fill from the active session when present.
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const topic = params.get("topic");
        const angel = params.get("angel");
        const name = (member && member.fullName) || "";
        const email = (member && member.email) || "";
        let message = "";
        if (topic === "reselect") {
            message = `Hi Angel Squad,\n\nI'd like to request a change to my Angel selection${angel ? ` (currently Angel ${angel})` : ""}.\n\nReason:\n`;
        }
        setForm((s) => ({
            name: s.name || name,
            email: s.email || email,
            message: s.message || message,
        }));
        // We intentionally only run once on mount per location change.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.search, member?.email, member?.fullName]);

    const onChange = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

    const onSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        setError("");
        try {
            await api.post("/contact", form);
            setDone(true);
            setForm({ name: "", email: "", message: "" });
            toast.success("Your message has been received.");
        } catch (e) {
            setError(formatApiError(e.response?.data?.detail) || "Could not send your message.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <PortalLayout>
            <section className="max-w-3xl mx-auto px-6 md:px-12 pt-20 pb-32" data-testid="contact-page">
                <span className="font-label text-[11px] text-[#cfa052]">Speak with us</span>
                <h1 className="mt-4 font-serif-display text-5xl md:text-6xl tracking-tighter text-[#e8e4db]" data-testid="contact-title">
                    A quiet line, <span className="italic">always open</span>
                </h1>
                <p className="mt-6 font-poetic text-lg text-[#9a958a] max-w-xl">
                    Questions, troubles, requests to re-choose your Angel — leave a note and the squad will reply.
                </p>

                {done ? (
                    <div className="mt-12 border border-[#cfa052]/40 bg-[#151311] p-8 rounded-sm" data-testid="contact-success">
                        <p className="font-poetic text-xl text-[#e8e4db]">Thank you. We've received your message.</p>
                        <p className="mt-2 font-poetic text-[#9a958a]">Expect a reply within a few days.</p>
                        <button
                            onClick={() => setDone(false)}
                            className="mt-6 gold-link font-label text-[10px]"
                            data-testid="contact-send-another"
                        >
                            Send another →
                        </button>
                    </div>
                ) : (
                    <form onSubmit={onSubmit} className="mt-12 space-y-8" data-testid="contact-form">
                        <div>
                            <label className="font-label text-[10px] text-[#cfa052]">Name</label>
                            <input
                                required
                                value={form.name}
                                onChange={onChange("name")}
                                className="mt-2 w-full bg-transparent border-b border-white/20 text-[#e8e4db] focus:border-[#cfa052] focus:outline-none py-3 transition-colors"
                                data-testid="contact-name-input"
                            />
                        </div>
                        <div>
                            <label className="font-label text-[10px] text-[#cfa052]">Email</label>
                            <input
                                required
                                type="email"
                                value={form.email}
                                onChange={onChange("email")}
                                className="mt-2 w-full bg-transparent border-b border-white/20 text-[#e8e4db] focus:border-[#cfa052] focus:outline-none py-3 transition-colors"
                                data-testid="contact-email-input"
                            />
                        </div>
                        <div>
                            <label className="font-label text-[10px] text-[#cfa052]">Message</label>
                            <textarea
                                required
                                rows={6}
                                value={form.message}
                                onChange={onChange("message")}
                                className="mt-2 w-full bg-transparent border-b border-white/20 text-[#e8e4db] focus:border-[#cfa052] focus:outline-none py-3 font-poetic resize-none transition-colors"
                                data-testid="contact-message-input"
                            />
                        </div>

                        {error && (
                            <div className="font-poetic text-sm text-[#d97c6a]" data-testid="contact-error">{error}</div>
                        )}

                        <button
                            type="submit"
                            disabled={submitting}
                            data-testid="contact-submit-btn"
                            className="bg-[#cfa052] hover:bg-[#e0b569] text-[#0c0b0a] px-8 py-4 font-label text-xs tracking-widest rounded-sm transition-colors duration-300 disabled:opacity-60"
                        >
                            {submitting ? "Sending…" : "Send message"}
                        </button>
                    </form>
                )}
            </section>
        </PortalLayout>
    );
}
